/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonModal,
  IonPopover,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import { useEffect, useState } from "react";
import { userAtom } from "@state/State";
import * as DatabaseService from "@controllers/DatabaseService";
import _ from "lodash";
import { format, isToday } from "date-fns";
import DataController from "@controllers/DataController";
import "./ProgramComponents.css";
import { useRecoilState } from "recoil";
import HeaderTitle from "@components/common/HeaderTitle";
import {
  AWSAccessParams,
  CompetenceActivityClass,
  ICertificateInfo,
  IUser,
  IUserProgramCertificate,
  ProgramCertificate,
  ProgramData,
} from "@interfaces/Interfaces";
import { Capacitor } from "@capacitor/core";
import { useFilePicker } from "use-file-picker";
import RemoteDataController from "@controllers/RemoteDataController";
import AWSService from "@controllers/AWSService";
import * as FirebaseService from "@controllers/FirebaseService";
import { EventRegister } from "react-native-event-listeners";
import { CERTIFICATE_ACCEPTED_FILE_TYPES, CERTIFICATE_MAX_FILE_SIZE } from "../../Constants";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import WeekDayRenderer from "./evidenceParts/WeekdayRenderer";
import * as StringUtils from "@utils/stringUtils";
import WriteBlob from "capacitor-blob-writer";
import { FileOpener } from "@capacitor-community/file-opener";
import { Directory } from "@capacitor/filesystem";
import mime from "mime";
import * as ProgramUtils from "@utils/programUtils";
import classNames from "classnames";
import validator from "validator";
import { sendCertificateVerificationRequest } from "@controllers/certificateVerification";
import ContentContainer from "@components/common/ContentContainer";
import * as CertificateUtils from "@utils/certificateUtils";

const defaultClassNames = getDefaultClassNames();
type Props = {
  visible: boolean;
  closeModal: (value: boolean) => void;
  certificate: ProgramCertificate | any;
  program: ProgramData | any;
  competence?: CompetenceActivityClass | any;
  role?: string;
  programCertificates?: IUserProgramCertificate[];
};

const CertificateModal: React.FC<Props> = (props) => {
  const [openFileSelector, { filesContent, clear, plainFiles }] = useFilePicker({
    accept: CERTIFICATE_ACCEPTED_FILE_TYPES,
    maxFileSize: CERTIFICATE_MAX_FILE_SIZE,
    readAs: "DataURL",
    multiple: false,
    limitFilesConfig: { max: 1 },
  });

  const [user, setUser] = useRecoilState<IUser | null>(userAtom);

  const [date, setDate] = useState<Date>(new Date());
  const [uploadedCertificate, setUploadedCertificate] = useState<any>(null);
  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [upload, setUpload] = useState<ICertificateInfo | null>(null);
  const [viewingCertificate, setViewingCertificate] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [verificationDatePickerVisible, setVerificationDatePickerVisible] = useState<boolean>(false);
  const [verificationRequest, setVerificationRequest] = useState<{
    trainerName: string;
    verificationEmail: string;
    verificationDate: Date;
  }>({
    trainerName: "",
    verificationEmail: "",
    verificationDate: new Date(),
  });

  const defaultCertificateMode = ProgramUtils.getCertificateMode(props.certificate?.Type);
  const showCertificateModeToggle = ProgramUtils.showCertificateModeToggle(props.certificate?.Type);
  const [selectedCertificateMode, setSelectedCertificateMode] = useState<"certificate" | "verification">(
    defaultCertificateMode
  );
  const certificateMode = showCertificateModeToggle ? selectedCertificateMode : defaultCertificateMode;
  const [addToEvidencePromptVisible, setAddToEvidencePromptVisible] = useState<boolean>(false);

  useEffect(() => {
    if (plainFiles.length > 0 && filesContent.length > 0) {
      const _certificate: ICertificateInfo = {
        name: plainFiles[0].name,
        type: plainFiles[0].type,
        size: plainFiles[0].size,
        content: filesContent[0].content,
        lastModified: plainFiles[0].lastModified,
        certificateType: props.certificate.Name,
      };

      setUpload(_certificate);
      clear();
    }
  }, [props.certificate, clear, filesContent, plainFiles]);

  const resetModal = (): void => {
    setDate(new Date());
    setUploadedCertificate(null);
    setDatePickerVisible(false);
    setUploading(false);
    setUpload(null);
    setVerificationRequest({
      trainerName: "",
      verificationEmail: "",
      verificationDate: new Date(),
    });
    setSelectedCertificateMode(defaultCertificateMode);
    setAddToEvidencePromptVisible(false);
    props.closeModal(false);
  };

  const openDatePicker = (): void => {
    setDatePickerVisible(true);
  };

  const openVerificationDatePicker = (): void => {
    setVerificationDatePickerVisible(true);
  };

  const viewCertificate = async (item: IUserProgramCertificate): Promise<void> => {
    try {
      setViewingCertificate(true);
      setDisabled(true);

      const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user!);

      const file = await AWSService.openCertificateFromServer(user!, accessParams, item.certificate);

      if (await DataController.isWebAsync()) {
        const url = window.URL.createObjectURL(
          new Blob([new Uint8Array(file.Body.data)], {
            type: file.ContentType,
          })
        );
        window.open(url, "_blank");
      } else {
        const blob = new Blob([new Uint8Array(file.Body.data)], {
          type: file.ContentType,
        });

        const filePath = await WriteBlob({
          path: `ParaFolio/Certificates/${item.certificate?.name}.${mime.getExtension(file.ContentType)}`,
          blob,
          directory: Directory.Library,
          recursive: true,
        });

        FileOpener.open({
          filePath,
        });
      }

      setViewingCertificate(false);
      setDisabled(false);
    } catch (error) {
      console.log(error);
      setViewingCertificate(false);
      setDisabled(false);
      window.alert("An error occurred when opening certificate");
    }
  };

  const requestVerification = async (): Promise<boolean> => {
    try {
      if (!user) return false;

      setUploading(true);

      let uploadResponse: any;

      if (upload) {
        const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user);

        uploadResponse = await AWSService.uploadCertificate(user, accessParams, upload);

        if (uploadResponse && uploadResponse.networkIssue) {
          uploadResponse = await AWSService.uploadCertificateFromServer(user, accessParams, upload);
        }
      }

      const userToSave = _.cloneDeep(user);
      const _certificates = _.cloneDeep(userToSave.programCertificates) || [];

      const _certificate: IUserProgramCertificate = {
        certificate:
          upload && uploadResponse
            ? {
                ...upload,
                content: undefined,
                url: uploadResponse.Location,
              }
            : undefined,
        issueDate: verificationRequest.verificationDate.toISOString(),
        verificationStatus: {
          status: "Pending",
          verificationDate: verificationRequest.verificationDate.toISOString(),
          trainerName: verificationRequest.trainerName,
        },
        programData: {
          programName: props.program.Name,
          programID: props.program.ID,
          ...(props.role && { role: props.role }),
          ...(props.competence && { competenceName: props.competence.Name }),
          ...((props.certificate as ProgramCertificate).ID && {
            certificateID: props.certificate.ID,
            certificateType: props.certificate.Name,
          }),
        },
      };

      const index = _certificates?.findIndex(
        (item) => item.programData.certificateID === _certificate.programData.certificateID
      );

      if (index > -1) {
        _certificates.splice(index, 1, _certificate);
      } else {
        _certificates.push(_certificate);
      }

      userToSave.programCertificates = _certificates;

      await DatabaseService.updateUserDetails(userToSave, {
        programCertificates: userToSave.programCertificates,
      });

      await sendCertificateVerificationRequest({
        user: userToSave,
        trainerName: verificationRequest.trainerName,
        trainerEmail: verificationRequest.verificationEmail,
        certificate: props.certificate,
        certificateData: {
          date: verificationRequest.verificationDate.toISOString(),
          programID: props.program.ID,
          certificateID: props.certificate.ID,
        },
      });

      FirebaseService.logEvent("program_certificate_verification_requested", {
        program: props.program?.Name,
        certificate: props.certificate?.Name,
      });

      setUser(userToSave);
      setVerificationRequest({
        trainerName: "",
        verificationEmail: "",
        verificationDate: new Date(),
      });
      setSelectedCertificateMode(defaultCertificateMode);
      setUploading(false);
      return true;
    } catch (error) {
      console.log(error);
      window.alert("An error occurred when requesting verification");
      return false;
    } finally {
      setUploading(false);
    }
  };

  const addVerification = async (): Promise<void> => {
    const saved = await requestVerification();
    if (saved) {
      if (ProgramUtils.shouldPromptAddCertificateToEvidence(props.program.ID)) {
        setAddToEvidencePromptVisible(true);
      } else {
        resetModal();
      }
    }
  };

  const addCertificate = async (): Promise<void> => {
    const saved = await persistCertificate();
    if (saved) {
      if (ProgramUtils.shouldPromptAddCertificateToEvidence(props.program.ID)) {
        setAddToEvidencePromptVisible(true);
      } else {
        resetModal();
      }
    }
  };

  const openEvidenceModalForCertificate = (): void => {
    if (!user) {
      return;
    }

    const data = ProgramUtils.buildProgramEvidenceModalData(user, props.program, {
      preselectedTitle: props.certificate.Name,
    });

    FirebaseService.logEvent("program_add_enhanced_evidence", {
      program: props.program?.Name,
    });

    EventRegister.emit("evidence/add-program-evidence", data);
  };

  const confirmAddToEvidence = (): void => {
    setAddToEvidencePromptVisible(false);
    openEvidenceModalForCertificate();
    resetModal();
  };

  const declineAddToEvidence = (): void => {
    setAddToEvidencePromptVisible(false);
    resetModal();
  };

  const persistCertificate = async (): Promise<boolean> => {
    try {
      if (user && upload) {
        setUploading(true);
        const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user);

        let response = await AWSService.uploadCertificate(user, accessParams, upload);

        if (response && response.networkIssue) {
          response = await AWSService.uploadCertificateFromServer(user, accessParams, upload);
        }

        if (response) {
          const userToSave = _.cloneDeep(user);
          const _certificates = _.cloneDeep(userToSave.programCertificates) || [];

          const _certificate: IUserProgramCertificate = {
            certificate: {
              ...upload,
              content: undefined,
              url: response.Location,
            },
            issueDate: date.toISOString(),
            programData: {
              programName: props.program.Name,
              programID: props.program.ID,
              ...(upload.certificateType && { certificateType: upload.certificateType }),
              ...(props.role && { role: props.role }),
              ...(props.competence && { competenceName: props.competence.Name }),
              ...((props.certificate as ProgramCertificate).ID && { certificateID: props.certificate.ID }),
            },
          };

          const index = _certificates?.findIndex(
            (item) =>
              item.certificate?.certificateType === _certificate.certificate?.certificateType ||
              item.programData.certificateID === _certificate.programData.certificateID
          );

          if (index > -1) {
            _certificates.splice(index, 1, _certificate);
          } else {
            _certificates.push(_certificate);
          }

          userToSave.programCertificates = _certificates;

          await DatabaseService.updateUserDetails(userToSave, {
            programCertificates: userToSave.programCertificates,
          });

          FirebaseService.logEvent("program_certificate_uploaded", {
            program: props.program?.Name,
            certificate: props.certificate?.Name,
          });

          setUpload(null);
          setUser(userToSave);
          setUploadedCertificate(_certificate);
          setUploading(false);
          return true;
        }
      }

      setUploading(false);
      window.alert("An error occurred when uploading the certificate. Please try again.");
      return false;
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      setUploading(false);
      window.alert("An error occurred when uploading the certificate. Please try again.");
      return false;
    }
  };

  const trainerNameChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setVerificationRequest({
      ...verificationRequest,
      trainerName: event.target.value,
    });
  };

  const verificationDateChanged = (data: Date): void => {
    setVerificationRequest({
      ...verificationRequest,
      verificationDate: data,
    });
  };

  const verificationEmailChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setVerificationRequest({
      ...verificationRequest,
      verificationEmail: event.target.value,
    });
  };

  const verificationDisabled = (): boolean => {
    const trainerNameEmpty = verificationRequest.trainerName.trim() === "";
    const verificationEmailEmpty = verificationRequest.verificationEmail.trim() === "";
    const verificationEmailInvalid = !validator.isEmail(verificationRequest.verificationEmail);

    return trainerNameEmpty || verificationEmailEmpty || verificationEmailInvalid || uploading;
  };

  const existingCertificate = props.programCertificates?.find((item) => {
    if (item.programData.role === props.role) {
      return item.programData.certificateID === props.certificate?.ID;
    }
  });

  return (
    <>
      <IonModal
        id="certificateModal"
        isOpen={props.visible}
        onIonModalDidDismiss={() => resetModal()}
        backdropDismiss={true}
        canDismiss={true}
        className={"fullscreen"}
      >
        <IonHeader>
          <IonToolbar
            mode="ios"
            className="navBar"
            style={{
              maxWidth: DataController.isWeb() ? 980 : undefined,
              height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
            }}
          >
            <IonButtons slot="start">
              <IonButton
                mode="ios"
                className="cancelButton"
                onClick={() => resetModal()}
              >
                {"Close"}
              </IonButton>
            </IonButtons>
            <HeaderTitle>{`Add ${props.program?.CertificateListHeader ?? "Certificate"}`}</HeaderTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="page-background whiteBackground">
          {props.certificate && (
            <ContentContainer
              className="!pt-0"
              style={{ paddingTop: 0, paddingBottom: 0, alignSelf: "center" }}
            >
              <div className="mx-auto max-w-app-column pt-4">
                <>
                  <div className="programAddCertificateTitle">{`${props.certificate.Name}`}</div>
                  <CertificateModalSubtitle {...props} />
                  {showCertificateModeToggle && (
                    <div className="mb-4 rounded-md border-[1px] border-solid border-grey-40 bg-white px-3 pb-2 pt-4">
                      <div className={classNames("programAddCertificate whitespace-pre-wrap text-14px")}>
                        {"Select training type"}
                      </div>
                      <IonRadioGroup
                        color="light"
                        id="certificateModeRadioGroup"
                        value={certificateMode}
                        onIonChange={() =>
                          setSelectedCertificateMode(certificateMode === "certificate" ? "verification" : "certificate")
                        }
                      >
                        <IonItem className="evidencePartsRadioContainer">
                          <IonRadio
                            aria-label="Certificate"
                            mode="md"
                            value="certificate"
                            justify="space-between"
                            className={classNames("evidencePartsRadioLabel text-wrap text-17px tracking-[0.]", {
                              "font-semibold": certificateMode === "certificate",
                            })}
                          >
                            {CertificateUtils.getCertificateExternalString(props.program.ID)}
                          </IonRadio>
                        </IonItem>
                        <IonItem className="evidencePartsRadioContainer">
                          <IonRadio
                            aria-label="Verification"
                            mode="md"
                            value="verification"
                            justify="space-between"
                            className={classNames("evidencePartsRadioLabel text-wrap text-17px tracking-[0.]", {
                              "font-semibold": certificateMode === "verification",
                            })}
                          >
                            {CertificateUtils.getCertificateInternalString(props.program.ID)}
                          </IonRadio>
                        </IonItem>
                      </IonRadioGroup>
                    </div>
                  )}
                  {certificateMode === "certificate" && (
                    <>
                      {existingCertificate && existingCertificate.certificate && (
                        <div className="mb-3 rounded-md border-[1px] border-solid border-grey-40 bg-white px-3 pb-2 pt-4">
                          <div className="programAddCertificate">{"Current certificate"}</div>
                          <button
                            disabled={disabled}
                            key={existingCertificate.certificate.name}
                            onClick={() => !(disabled || viewingCertificate) && viewCertificate(existingCertificate)}
                            className={`program-certificate-button pointer-events-auto mb-3 mr-[8px] flex !min-h-[26px] min-w-0 flex-[-1] flex-row items-center self-start bg-transparent ${viewingCertificate && "program-certificate-button-wait"} ${disabled && "program-certificate-button-disabled"}`}
                          >
                            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[16px] leading-default tracking-default text-cta-blue">
                              {existingCertificate.certificate.name}
                            </div>
                            {viewingCertificate && <IonSpinner className="ml-[8px] h-[15px] w-[15px] text-cta-blue" />}
                          </button>
                          <div className="programAddCertificate">{"Current Issue Date"}</div>
                          <div className="">
                            {existingCertificate.issueDate
                              ? format(new Date(existingCertificate.issueDate), "dd / MM / yyyy")
                              : "No Issue Date"}
                          </div>
                          {ProgramUtils.shouldPromptAddCertificateToEvidence(props.program.ID) && (
                            <div className="programAddCertificateButtonContainer mt-3">
                              <IonButton
                                mode="ios"
                                onClick={confirmAddToEvidence}
                                disabled={false}
                                className="programAddCertificateButton"
                              >
                                <div>{"Add evidence about this training"}</div>
                              </IonButton>
                            </div>
                          )}
                        </div>
                      )}
                      <div>
                        <div className="programAddCertificate">{`${existingCertificate && existingCertificate.certificate ? "New " : ""}Certificate`}</div>
                        <div
                          className="onboardingDateContainer"
                          style={{ marginBottom: 12 }}
                          onClick={() => openFileSelector()}
                        >
                          <div
                            className="programAddCertificateName acc-underline"
                            title={(uploadedCertificate?.certificate.name || upload?.name) ?? undefined}
                          >
                            {uploadedCertificate?.certificate.name || upload?.name || "Tap to add certificate"}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="programAddCertificate">
                          {`${existingCertificate && existingCertificate.certificate ? "New " : ""}${CertificateUtils.getVerificationDateTitle(props.program.ID)}`}
                        </div>
                        <div
                          id="certificateDatePicker"
                          className="onboardingDateContainer"
                          style={{ marginBottom: 12 }}
                          onClick={() => openDatePicker()}
                        >
                          <div className="acc-underline text-cta-blue">{format(date, "dd / MM / yyyy")}</div>
                          <IonPopover
                            trigger="certificateDatePicker"
                            id="certificate-date-picker"
                            isOpen={datePickerVisible}
                            onDidDismiss={() => setDatePickerVisible(false)}
                          >
                            <div className="p-5 pt-2">
                              <DayPicker
                                mode="single"
                                weekStartsOn={1}
                                selected={date}
                                defaultMonth={date}
                                onSelect={(data: Date | undefined) => {
                                  if (data) {
                                    setDate(data);
                                    setDatePickerVisible(false);
                                  }
                                }}
                                endMonth={new Date()}
                                disabled={{ after: new Date() }}
                                classNames={{
                                  selected: `bg-cta-blue text-white rounded-[10px]`,
                                  today: isToday(date) ? "text-white" : getDefaultClassNames().today,
                                  month_caption: `${defaultClassNames.month_caption} text-[#595959] font-normal`,
                                }}
                                components={{
                                  Weekday: (el) => <WeekDayRenderer element={el} />,
                                }}
                              />
                            </div>
                          </IonPopover>
                        </div>
                      </div>
                      <div>
                        <div className="programAddCertificate">{"Duration"}</div>
                        <div className="onboardingDateContainer mb-3 !cursor-auto">
                          <div className="onboardingDateText">
                            {StringUtils.getCertificateDurationString(props.certificate.Duration)}
                          </div>
                        </div>
                      </div>
                      <div className="programAddCertificateButtonContainer">
                        <IonButton
                          mode="ios"
                          onClick={() => addCertificate()}
                          disabled={!upload || uploading}
                          className="programAddCertificateButton"
                        >
                          <div>{uploading ? <IonSpinner className="reflectionSaveSpinner" /> : <>{"Add"}</>}</div>
                        </IonButton>
                      </div>
                    </>
                  )}
                  {certificateMode === "verification" && (
                    <>
                      <div>
                        {existingCertificate && existingCertificate.certificate && (
                          <div className="mb-3 rounded-md border-[1px] border-solid border-grey-40 bg-white px-3 pb-2 pt-4">
                            <div className="programAddCertificate">{"Current certificate"}</div>
                            <button
                              disabled={disabled}
                              key={existingCertificate.certificate.name}
                              onClick={() => !(disabled || viewingCertificate) && viewCertificate(existingCertificate)}
                              className={`program-certificate-button pointer-events-auto mb-3 mr-[8px] flex !min-h-[26px] min-w-0 flex-[-1] flex-row items-center self-start bg-transparent ${viewingCertificate && "program-certificate-button-wait"} ${disabled && "program-certificate-button-disabled"}`}
                            >
                              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[16px] leading-default tracking-default text-cta-blue">
                                {existingCertificate.certificate.name}
                              </div>
                              {viewingCertificate && (
                                <IonSpinner className="ml-[8px] h-[15px] w-[15px] text-cta-blue" />
                              )}
                            </button>
                            <div className="programAddCertificate">{"Current Issue Date"}</div>
                            <div className="">
                              {existingCertificate.issueDate
                                ? format(new Date(existingCertificate.issueDate), "dd / MM / yyyy")
                                : "No Issue Date"}
                            </div>
                            {ProgramUtils.shouldPromptAddCertificateToEvidence(props.program.ID) && (
                              <div className="programAddCertificateButtonContainer mt-3">
                                <IonButton
                                  mode="ios"
                                  onClick={confirmAddToEvidence}
                                  disabled={false}
                                  className="programAddCertificateButton"
                                >
                                  <div>{"Add evidence about this training"}</div>
                                </IonButton>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="programAddCertificate">
                          {CertificateUtils.getTrainerNameTitle(props.program.ID)}
                        </div>
                        <div>
                          <input
                            className="h-12.5 w-full rounded-lg border-[0.5px] border-solid border-grey-50 bg-grey-10 px-3 text-grey-90 placeholder:text-grey-60"
                            placeholder={CertificateUtils.getTrainerNamePlaceholder(props.program.ID)}
                            autoComplete="off"
                            value={verificationRequest.trainerName}
                            onChange={(event) => trainerNameChanged(event)}
                          />
                        </div>
                        <div className="programAddCertificate mt-3">
                          {`${existingCertificate && existingCertificate.certificate ? "New " : ""}${CertificateUtils.getVerificationDateTitle(props.program.ID)}`}
                        </div>
                        <div
                          id="verificationDatePicker"
                          className="onboardingDateContainer"
                          onClick={() => openVerificationDatePicker()}
                        >
                          <div className="acc-underline text-cta-blue">
                            {format(verificationRequest.verificationDate, "dd / MM / yyyy")}
                          </div>
                          <IonPopover
                            trigger="verificationDatePicker"
                            id="certificate-date-picker"
                            isOpen={verificationDatePickerVisible}
                            onDidDismiss={() => setVerificationDatePickerVisible(false)}
                          >
                            <div className="p-5 pt-2">
                              <DayPicker
                                mode="single"
                                weekStartsOn={1}
                                selected={verificationRequest.verificationDate}
                                defaultMonth={verificationRequest.verificationDate}
                                onSelect={(data: Date | undefined) => {
                                  if (data) {
                                    verificationDateChanged(data);
                                    setVerificationDatePickerVisible(false);
                                  }
                                }}
                                endMonth={new Date()}
                                disabled={{ after: new Date() }}
                                classNames={{
                                  selected: `bg-cta-blue text-white rounded-[10px]`,
                                  today: isToday(verificationRequest.verificationDate)
                                    ? "text-white"
                                    : getDefaultClassNames().today,
                                  month_caption: `${defaultClassNames.month_caption} text-[#595959] font-normal`,
                                }}
                                components={{
                                  Weekday: (el) => <WeekDayRenderer element={el} />,
                                }}
                              />
                            </div>
                          </IonPopover>
                        </div>
                        <div className="programAddCertificate mt-3">{"Duration"}</div>
                        <div className="onboardingDateContainer mb-3 !cursor-auto">
                          <div className="onboardingDateText">
                            {StringUtils.getCertificateDurationString(props.certificate.Duration)}
                          </div>
                        </div>
                        <div className="programAddCertificate mt-3">
                          {CertificateUtils.getVerificationEmailTitle(props.program.ID)}
                        </div>
                        <div>
                          <input
                            className="h-12.5 w-full rounded-lg border-[0.5px] border-solid border-grey-50 bg-grey-10 px-3 text-grey-90 placeholder:text-grey-60"
                            placeholder={CertificateUtils.getVerificationEmailPlaceholder(props.program.ID)}
                            autoComplete="off"
                            value={verificationRequest.verificationEmail}
                            onChange={(event) => verificationEmailChanged(event)}
                          />
                        </div>
                        <div className="programAddCertificate mt-3">
                          {CertificateUtils.getCertificateSignOffFormTitle(props.program.ID)}
                        </div>
                        <div
                          className="onboardingDateContainer"
                          onClick={() => openFileSelector()}
                        >
                          <div
                            className="programAddCertificateName acc-underline"
                            title={(uploadedCertificate?.name || upload?.name) ?? undefined}
                          >
                            {uploadedCertificate?.certificate.name || upload?.name || "Tap to add form"}
                          </div>
                        </div>
                      </div>
                      <div className="programAddCertificateButtonContainer mt-3">
                        <IonButton
                          mode="ios"
                          onClick={() => addVerification()}
                          disabled={verificationDisabled()}
                          className="programAddCertificateButton"
                        >
                          <div>
                            {uploading ? (
                              <IonSpinner className="reflectionSaveSpinner" />
                            ) : (
                              <>{"Request verification"}</>
                            )}
                          </div>
                        </IonButton>
                      </div>
                    </>
                  )}
                </>
              </div>
            </ContentContainer>
          )}
        </IonContent>
      </IonModal>
      <IonModal
        id="add-certificate-to-evidence-modal"
        isOpen={addToEvidencePromptVisible}
        backdropDismiss={false}
        canDismiss={true}
        className="fullscreen"
      >
        <IonContent>
          <div className="flex h-full flex-col justify-between">
            <div className="mt-[16px] px-[16px]">
              <div className="text-center text-[20px] font-bold leading-default tracking-[-0.1px] text-grey-90">
                {"Certificate Added!"}
              </div>
              <div className="text-center text-[20px] font-bold leading-default tracking-[-0.1px] text-grey-90">
                {"Add Evidence about your Training?"}
              </div>
              <div className="mt-[8px] text-center text-[15px] leading-[1.33] tracking-[-0.3px] text-grey-90">
                {"Describe your training and add NOS"}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="border-t border-t-grey-30">
                <button
                  className="h-[48px] w-full"
                  onClick={confirmAddToEvidence}
                >
                  <div className="text-center text-[16px] font-semibold leading-default tracking-default text-cta-blue">
                    {"Yes"}
                  </div>
                </button>
              </div>
              <div className="border-t border-t-grey-30">
                <button
                  className="h-[48px] w-full"
                  onClick={declineAddToEvidence}
                >
                  <div className="text-center text-[16px] leading-default tracking-default text-cta-blue">{"No"}</div>
                </button>
              </div>
            </div>
          </div>
        </IonContent>
      </IonModal>
    </>
  );
};

const CertificateModalSubtitle: React.FC<Props> = (props) => {
  if (props.program.CertificateModalSubtitle) {
    return (
      <div
        className="programAddCertificateDescription"
        dangerouslySetInnerHTML={StringUtils.sanitizeHTML(props.program.CertificateModalSubtitle)}
      />
    );
  }

  if (props.certificate.Description) {
    return (
      <div
        className="programAddCertificateDescription"
        dangerouslySetInnerHTML={StringUtils.sanitizeHTML(props.certificate.Description)}
      />
    );
  }

  return (
    <div className="programAddCertificateDescription">
      {"Please upload a valid certificate that you hold to practise this enhanced skill."}
    </div>
  );
};

export default CertificateModal;
